import type { PipeIntegrationDef, PipeIntegration } from '@render/integrations'
import type { PipeInterface } from '@render/composables/usePipe'
import { watch } from 'vue'
import { loadYunoSdk, YUNO_ORIGINS } from '@render/integrations/yuno/yunoSdk'

// Yuno pre-warm integration — warms the CDN/API connections and loads the SDK
// bundle before the user reaches the checkout page. Without it, YunoCheckout's
// mount pays for a cold third-party origin (DNS + TLS + TCP, then download,
// parse and execute) on the critical path, and it pays for it AFTER the session
// POST has resolved, because useYuno only reaches loadYunoSdk() inside
// renderYunoCheckout. Auto-activated at build time when YunoCheckout is used in
// the pipe (see builder/lib/pages.ts getActiveIntegrations).
//
// There is deliberately no rel=preload here, unlike the Primer integration.
// Primer's npm package is a loader shim whose real bundle (PrimerCoreSDK.min.js)
// is fetched later by showUniversalCheckout, so preloading that second URL is
// the only way to warm it. Yuno's package IS the loader: loadYunoSdk() injects
// the one bundle through the package's own loadScript(). A hand-written preload
// would have to hardcode a URL the package derives itself from its LATEST_VERSION
// constant, so it would silently point at the wrong version on the next package
// bump — warming a bundle nobody then loads.
const yuno: PipeIntegration = (): PipeIntegrationDef => {
  return {
    setup(pipe: PipeInterface) {
      if (typeof window === 'undefined') return

      // Warm TCP/TLS to Yuno's origins immediately on setup — before any user
      // interaction, and before the email watcher below can fire.
      for (const { href, anonymous } of YUNO_ORIGINS) {
        const dns = document.createElement('link')
        dns.rel = 'dns-prefetch'
        dns.href = href
        document.head.appendChild(dns)

        const preconnect = document.createElement('link')
        preconnect.rel = 'preconnect'
        preconnect.href = href
        if (anonymous) preconnect.crossOrigin = 'anonymous'
        document.head.appendChild(preconnect)
      }

      // The SDK chunk load DOES retry. An offline visitor fails it right at the
      // email step (this watcher's trigger); without a retry the checkout would
      // later mount against a dead loader. loadYunoSdk never caches a rejection,
      // so re-running loadAssets re-attempts the load.
      let sdkLoaded = false
      let onlineRetryArmed = false

      const loadAssets = () => {
        if (sdkLoaded) return

        // Resolve the SDK now so the script is already executed and
        // `window.SdkPayments` is live by the time YunoCheckout mounts and calls
        // loadYunoSdk() itself. Injecting the bundle does not initialize
        // anything, touch the mount containers or create a session — it is inert
        // until Yuno.initialize() runs.
        loadYunoSdk()
          .then(() => {
            sdkLoaded = true
          })
          .catch((err) => {
            // Offline (or a CDN hiccup) during the pre-warm. loadYunoSdk has
            // already cleared its singleton, so retry the moment the browser
            // reports connectivity back. The checkout mount retries on its own
            // too — this just front-loads the recovery.
            console.warn('[yuno] SDK pre-warm failed — will retry when back online', err)
            if (onlineRetryArmed) return
            onlineRetryArmed = true
            window.addEventListener(
              'online',
              () => {
                onlineRetryArmed = false
                loadAssets()
              },
              { once: true },
            )
          })
      }

      // Trigger once email is known — user is past intake, heading to checkout.
      // Fires immediately for returning users with email in persisted state.
      watch(
        () => pipe.getData('var.global.email'),
        (email) => {
          if (typeof email === 'string' && email.trim().length > 0) loadAssets()
        },
        { immediate: true },
      )
    },
  }
}

export default yuno
